import { useLanguage, t } from "../../../i18n";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import { Row, Col } from "react-bootstrap";
import SectionHeader from "../../common/SectionHeader/SectionHeader";
import ListingCard from "../ListingCard/ListingCard";
import { useAuth } from "../../../features/auth/context/AuthContext";
import getFavouriteRoomListings from "../../../features/shortlist/services/getFavouriteRoomListings";
import "./RecentListingsSection.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
function NextArrow(props) {
  useLanguage();
  const {
    className,
    onClick
  } = props;
  return <button type="button" className={`${className} bb-recent-listings__arrow bb-recent-listings__arrow--next`} onClick={onClick} aria-label={t("Next listings")} />;
}
function PrevArrow(props) {
  useLanguage();
  const {
    className,
    onClick
  } = props;
  return <button type="button" className={`${className} bb-recent-listings__arrow bb-recent-listings__arrow--prev`} onClick={onClick} aria-label={t("Previous listings")} />;
}
function getViewportConfig(width, totalListings) {
  if (width < 576) {
    return {
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: false,
      infinite: totalListings > 1,
      isMobile: true
    };
  }
  if (width < 768) {
    return {
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: false,
      infinite: totalListings > 1,
      isMobile: true
    };
  }
  if (width < 992) {
    return {
      slidesToShow: Math.min(2, totalListings),
      slidesToScroll: 1,
      arrows: totalListings > 2,
      infinite: totalListings > 2,
      isMobile: false
    };
  }
  return {
    slidesToShow: Math.min(3, totalListings),
    slidesToScroll: 1,
    arrows: totalListings > 3,
    infinite: totalListings > 3,
    isMobile: false
  };
}
function extractFavouriteIds(response) {
  const favouritesArray = Array.isArray(response?.data?.data?.data) ? response.data.data.data : Array.isArray(response?.data?.data) ? response.data.data : Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
  return new Set(favouritesArray.map(item => {
    return item?.room_listing?.id || item?.id || item?.room_listing_id || item?.listing_id;
  }).filter(Boolean).map(value => String(value)));
}
function mergeFavouriteState(listings = [], favouriteIds = new Set()) {
  return listings.map(listing => {
    const listingId = listing?.id;
    const isFavouriteFromListing = Boolean(listing?.isShortlisted || listing?.is_favourite || listing?.raw?.is_favourite);
    const isFavouriteFromApi = favouriteIds.has(String(listingId));
    const nextIsFavourite = isFavouriteFromListing || isFavouriteFromApi;
    return {
      ...listing,
      isShortlisted: nextIsFavourite,
      is_favourite: nextIsFavourite,
      raw: {
        ...(listing?.raw || {}),
        is_favourite: nextIsFavourite
      }
    };
  });
}
export default function RecentListingsSection({
  listings = [],
  city
}) {
  useLanguage();
  const navigate = useNavigate();
  const {
    isAuthenticated
  } = useAuth();
  const [localListings, setLocalListings] = useState([]);
  const [viewportWidth, setViewportWidth] = useState(() => {
    if (typeof window === "undefined") return 1200;
    return window.innerWidth;
  });
  const [isSliderReady, setIsSliderReady] = useState(false);
  useEffect(() => {
    let isMounted = true;
    const syncListings = async () => {
      try {
        if (!isAuthenticated) {
          if (isMounted) {
            setLocalListings(Array.isArray(listings) ? listings : []);
          }
          return;
        }
        const favouritesResponse = await getFavouriteRoomListings();
        const favouriteIds = extractFavouriteIds(favouritesResponse);
        const mergedListings = mergeFavouriteState(Array.isArray(listings) ? listings : [], favouriteIds);
        if (isMounted) {
          setLocalListings(mergedListings);
        }
      } catch (error) {
        if (isMounted) {
          setLocalListings(Array.isArray(listings) ? listings : []);
        }
      }
    };
    syncListings();
    return () => {
      isMounted = false;
    };
  }, [listings, isAuthenticated]);
  useEffect(() => {
    let resizeTimer;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        setViewportWidth(window.innerWidth);
      }, 80);
    };
    handleResize();
    setIsSliderReady(true);
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);
  const handleListingShortlistChange = useCallback((nextIsShortlisted, updatedListing) => {
    if (!updatedListing?.id) return;
    setLocalListings(prev => prev.map(item => String(item.id) === String(updatedListing.id) ? {
      ...item,
      ...updatedListing,
      isShortlisted: nextIsShortlisted,
      is_favourite: nextIsShortlisted,
      raw: {
        ...(item.raw || {}),
        ...(updatedListing.raw || {}),
        is_favourite: nextIsShortlisted
      }
    } : item));
  }, []);
  const visibleListings = useMemo(() => {
    return localListings.slice(0, 10);
  }, [localListings]);
  const shouldUseSlider = visibleListings.length >= 3;
  const viewportConfig = useMemo(() => {
    return getViewportConfig(viewportWidth, visibleListings.length);
  }, [viewportWidth, visibleListings.length]);
  const sliderSettings = useMemo(() => ({
    dots: true,
    arrows: viewportConfig.arrows,
    infinite: viewportConfig.infinite,
    speed: 500,
    slidesToShow: viewportConfig.slidesToShow,
    slidesToScroll: viewportConfig.slidesToScroll,
    swipeToSlide: true,
    autoplay: false,
    adaptiveHeight: false,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    responsive: []
  }), [viewportConfig]);
  const sliderKey = `${viewportWidth}-${visibleListings.length}-${viewportConfig.slidesToShow}`;
  return <div className="bb-recent-listings-section">
      <SectionHeader title={t("Recent Room Listings")} subtitle={t("Showing recent room listings based on your current location: {0}", [t(city)])} actionLabel={t("View All")} onActionClick={() => navigate("/room-listings")} />


      {!visibleListings.length ? null : shouldUseSlider ? <div className={`bb-recent-listings-slider-wrap ${viewportConfig.isMobile ? "bb-recent-listings-slider-wrap--mobile" : ""}`}>

          {isSliderReady ? <Slider key={sliderKey} {...sliderSettings}>
              {visibleListings.map(listing => <div key={listing.id} className="bb-recent-listings-slider__item">
                  <div className="bb-recent-listings-slider__card-wrap">
                    <ListingCard listing={listing} onShortlistChange={handleListingShortlistChange} />

                  </div>
                </div>)}
            </Slider> : null}
        </div> : <Row className="g-4 bb-recent-listings-grid">
          {visibleListings.map(listing => <Col key={listing.id} xl={4} md={6} xs={12}>
              <div className="bb-recent-listings-grid__card-wrap">
                <ListingCard listing={listing} onShortlistChange={handleListingShortlistChange} />

              </div>
            </Col>)}
        </Row>}
    </div>;
}
